<template>
  <div class="app-content content">
    <div class="content-overlay" />
    <div class="header-navbar-shadow" />
    <div class="content-wrapper">

      <div
        v-if="!conversation"
        class="text-center py-3"
      >
        <span class="spinner-border" />
      </div>

      <template v-else>
        <!-- Header -->
        <div class="content-header row">
          <div class="content-header-left col-md-9 col-12 mb-2">
            <div class="row breadcrumbs-top">
              <div class="col-12">
                <h2 class="content-header-title mb-0">
                  <span class="text-muted small me-50">{{ conversation.code }}</span>
                  {{ conversation.title }}
                  <small><span :class="statusBadge(conversation.status)" class="ms-50">{{ statusLabel(conversation.status) }}</span></small>
                </h2>
                <div class="mt-25 text-muted small">
                  Opened {{ formatDate(conversation.created_at) }}
                </div>
              </div>
            </div>
          </div>
          <div class="content-header-right text-md-end col-md-3 col-12 d-md-block d-none">
            <div class="mb-1 breadcrumb-right">
              <a
                class="btn btn-icon btn-secondary"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title="Back to OSH Conversations"
                @click="$router.push({ name: 'researcher.conversations.index', query: { actAs: $route.query.actAs } })"
              ><i data-feather="chevron-left" /></a>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <!-- Messages thread -->
            <div
              ref="thread"
              class="card"
              style="max-height: 520px; overflow-y: auto;"
            >
              <div class="card-body">
                <div
                  v-for="msg in conversation.messages"
                  :key="msg.id"
                  class="mb-2"
                  :class="msg.author && msg.author.id === currentUser.id ? 'd-flex flex-row-reverse' : 'd-flex'"
                >
                  <div
                    class="p-1 rounded"
                    style="max-width: 75%;"
                    :class="msg.author && msg.author.id === currentUser.id ? 'bg-primary text-white' : 'bg-light'"
                  >
                    <div class="small mb-25 fw-bold">
                      {{ msg.author && msg.author.id === currentUser.id ? 'You' : (msg.author ? msg.author.name : 'OSH Team') }}
                    </div>
                    <div style="white-space: pre-wrap;">{{ msg.body }}</div>
                    <div class="small mt-25 d-flex align-items-center gap-50">
                      <span :class="msg.author && msg.author.id === currentUser.id ? 'text-white-50' : 'text-muted'">
                        {{ formatDate(msg.created_at) }}
                      </span>
                      <span
                        v-if="msg.read_at && msg.author && msg.author.id === currentUser.id"
                        class="text-white-50"
                        title="Read by OSH team"
                      >
                        <i data-feather="check-circle" style="width:12px;height:12px;" />
                      </span>
                    </div>
                  </div>
                </div>

                <div
                  v-if="!conversation.messages || !conversation.messages.length"
                  class="text-center text-muted py-2"
                >
                  No messages yet.
                </div>
              </div>
            </div>

            <!-- Reply box -->
            <div
              v-if="conversation.status !== 'closed'"
              class="card"
            >
              <div class="card-body">
                <textarea
                  v-model="replyBody"
                  class="form-control mb-1"
                  rows="3"
                  placeholder="Write a message to the OSH team..."
                  :disabled="sending"
                />
                <div class="d-flex justify-content-end">
                  <button
                    class="btn btn-primary btn-sm"
                    :disabled="sending || !replyBody.trim()"
                    @click="sendReply"
                  >
                    <span v-if="sending"><span class="spinner-border spinner-border-sm me-25" />Sending...</span>
                    <span v-else><i data-feather="send" class="me-25" />Send message</span>
                  </button>
                </div>
              </div>
            </div>

            <div
              v-else
              class="alert alert-secondary"
            >
              This conversation has been closed by the OSH team.
            </div>
          </div>
        </div>
      </template>

    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'

export default {
  data() {
    return {
      replyBody: '',
      sending: false,
    }
  },
  computed: {
    ...mapGetters({
      conversation: 'conversations/conversation',
      currentUser: 'auth/admin',
    }),
  },
  async mounted() {
    await this.$store.dispatch('conversations/fetchConversation', this.$route.params.id)
    this.scrollToBottom()
    this.$nextTick(() => {
      // eslint-disable-next-line no-undef
      feather.replace({ width: 14, height: 14 })
    })
  },
  beforeDestroy() {
    this.$store.dispatch('conversations/clearConversation')
  },
  methods: {
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.thread
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    async sendReply() {
      if (!this.replyBody.trim()) return
      this.sending = true
      try {
        await this.$store.dispatch('conversations/sendMessage', {
          id: this.$route.params.id,
          body: this.replyBody.trim(),
        })
        this.replyBody = ''
        this.scrollToBottom()
        this.$nextTick(() => {
          // eslint-disable-next-line no-undef
          feather.replace({ width: 14, height: 14 })
        })
      } catch (e) {
        Vue.swal('Error', 'Could not send the message. Please try again.', 'error')
      } finally {
        this.sending = false
      }
    },
    statusBadge(status) {
      const map = { pending: 'badge bg-warning', replied: 'badge bg-info', closed: 'badge bg-secondary' }
      return map[status] || 'badge bg-light'
    },
    statusLabel(status) {
      const map = { pending: 'Pending', replied: 'Replied', closed: 'Closed' }
      return map[status] || status
    },
    formatDate(date) {
      if (!date) return '—'
      return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
  },
}
</script>
